import { type RefObject, useEffect, useLayoutEffect, useRef } from 'react';

export interface UseDismissableOverlayOptions {
  open: boolean;
  onClose: () => void;
}

export interface UseDismissableOverlayResult {
  /** The consumer assigns this to the focus-trappable container. */
  containerRef: RefObject<HTMLDivElement | null>;
}

/**
 * Shared focus mechanics for Modal, and later AppShell's drawer and user
 * menu: capture the element with focus before opening, move focus inside,
 * trap Tab/Shift+Tab within the container, close on Escape, return focus to
 * the trigger on close.
 */
export function useDismissableOverlay({
  open,
  onClose,
}: UseDismissableOverlayOptions): UseDismissableOverlayResult {
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<Element | null>(null);
  // Callers routinely pass an inline `onClose`, which is a new function on
  // every parent render. Reading it through a ref keeps the effect keyed on
  // `open` alone: otherwise each keystroke in a controlled field re-ran the
  // effect, whose cleanup and setup moved focus back to the trigger and then
  // to the first button.
  const onCloseRef = useRef(onClose);

  useLayoutEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;

    triggerRef.current = document.activeElement;
    const focusable = getFocusableElements(containerRef.current);
    (focusable[0] ?? containerRef.current)?.focus();

    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        onCloseRef.current();
        return;
      }

      if (event.key !== 'Tab') return;

      // Recalculated on every Tab, not cached: a field can become
      // enabled/disabled while the overlay stays open.
      const items = getFocusableElements(containerRef.current);
      const first = items[0];
      const last = items[items.length - 1];
      if (first === undefined || last === undefined) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      (triggerRef.current as HTMLElement | null)?.focus();
    };
  }, [open]);

  return { containerRef };
}

function getFocusableElements(container: HTMLElement | null): HTMLElement[] {
  if (container === null) return [];
  return Array.from(
    container.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  );
}
